import type { LucideIcon } from 'lucide-react';
import {
  CheckSquare,
  ClipboardList,
  Download,
  Contact,
  FileText,
  History,
  KeyRound,
  LayoutDashboard,
  Network,
  UserRound,
  Users,
} from 'lucide-react';

export const ROLE_ADMIN = 'ROLE_ADMIN';
export const ROLE_USER = 'ROLE_USER';
/** Rôle dans la société sélectionnée (sidebar), préfixé COMPANY_ : DIRECTOR => COMPANY_DIRECTOR (cf. useAccessRoles) */
export const COMPANY_ROLE_PREFIX = 'COMPANY_';
export const ROLE_COMPANY_DIRECTOR = `${COMPANY_ROLE_PREFIX}DIRECTOR`;
export const ROLE_COMPANY_ASSISTANT = `${COMPANY_ROLE_PREFIX}ASSISTANT`;
export const ROLE_COMPANY_EMPLOYEE = `${COMPANY_ROLE_PREFIX}EMPLOYEE`;
/** Manager actif d'au moins une équipe de la société sélectionnée (cf. useAccessRoles) */
export const ROLE_TEAM_MANAGER = 'TEAM_MANAGER';

/** Mot de passe local : modification, accessible à tout utilisateur connecté */
export const PASSWORD_PATH = '/account/password';
/** Première définition du mot de passe local, imposée tant qu'il n'est pas défini (RequireAuth), hors de l'application */
export const DEFINE_PASSWORD_PATH = '/account/password/define';
export const PROFILE_PATH = '/account/profile';

/**
 * adminSettings / directorSettings : « Paramétrage Administrateurs » / « Paramétrage Dirigeant » de la sidebar ;
 * account : pages du compte, hors menu principal (lien en bas de la sidebar) ; header : page ouverte depuis l'en-tête
 * (icône Téléchargements), hors de la sidebar
 */
export type NavSection = 'main' | 'adminSettings' | 'directorSettings' | 'account' | 'header';

export interface NavItem {
  path: string;
  label: string;
  description: string;
  icon: LucideIcon;
  section: NavSection;
  /** L'utilisateur doit avoir au moins un de ces rôles : rôle applicatif (ROLE_…) ou rôle dans la société sélectionnée (COMPANY_…) */
  roles: string[];
}

/**
 * Pages du backoffice (cf. cahier des charges §4 et prototype Figma).
 * Rôles applicatifs (ROLE_ADMIN / ROLE_USER) ou rôle dans la société sélectionnée (ROLE_COMPANY_DIRECTOR : équipes et employés,
 * gérés par les seuls Dirigeants, comme côté API) ; ROLE_TEAM_MANAGER : manager d'une équipe de la société sélectionnée
 * (Validation de ses équipes).
 */
export const NAV_ITEMS: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Tableau de bord',
    description: "Vue d'ensemble de la période en cours : demandes et heures en attente, heures traitées",
    icon: LayoutDashboard,
    section: 'main',
    // Dirigeant de la société sélectionnée, manager (ses équipes) ; pas l'admin, comme la Validation et l'Historique
    // (chef de projet, 2026-10-29)
    roles: [ROLE_COMPANY_DIRECTOR, ROLE_TEAM_MANAGER],
  },
  {
    path: '/timesheet',
    label: "Feuille d'heure",
    description: 'Déclaration de vos heures supplémentaires',
    icon: FileText,
    section: 'main',
    // Employés et assistants de la société sélectionnée, admins compris ; jamais le Dirigeant, qui ne déclare pas (§4.1)
    roles: [ROLE_COMPANY_EMPLOYEE, ROLE_COMPANY_ASSISTANT],
  },
  {
    path: '/requests',
    label: 'Mes Demandes',
    description: "Suivez l'état de vos demandes",
    icon: ClipboardList,
    section: 'main',
    // Comme la Feuille d'heure : les feuilles de l'employé dans la société sélectionnée
    roles: [ROLE_COMPANY_EMPLOYEE, ROLE_COMPANY_ASSISTANT],
  },
  {
    path: '/validation',
    label: 'Validation',
    description: 'Validez ou refusez les feuilles en attente et suivez la clôture de la période',
    icon: CheckSquare,
    section: 'main',
    // Dirigeant de la société sélectionnée (valide aussi à la place du manager) ; manager : ses équipes
    roles: [ROLE_COMPANY_DIRECTOR, ROLE_TEAM_MANAGER],
  },
  {
    path: '/history',
    label: 'Historique',
    description: 'Les périodes clôturées : heures déclarées et nombre de demandes',
    icon: History,
    section: 'main',
    // Dirigeant de la société sélectionnée, manager (les totaux de ses équipes) ; pas l'admin (chef de projet, 2026-10-26)
    roles: [ROLE_COMPANY_DIRECTOR, ROLE_TEAM_MANAGER],
  },
  {
    path: '/users',
    label: 'Utilisateurs',
    description: 'Gérez les accès et les rôles des collaborateurs',
    icon: Users,
    section: 'adminSettings',
    roles: [ROLE_ADMIN],
  },
  {
    path: '/teams',
    label: 'Équipes',
    description: "L'organigramme de la société : direction, équipes, managers et équipiers",
    icon: Network,
    section: 'directorSettings',
    roles: [ROLE_COMPANY_DIRECTOR],
  },
  {
    path: '/employees',
    label: 'Employés',
    description: 'Les employés de la société sélectionnée et leur rôle dans la société',
    icon: Contact,
    section: 'directorSettings',
    roles: [ROLE_COMPANY_DIRECTOR],
  },
  {
    path: '/exports',
    label: 'Exports',
    description: "Les exports des feuilles d'heures de la société : CSV et ZIP des PDF, à télécharger",
    icon: Download,
    section: 'header',
    // Pour tout le monde (icône Téléchargements de l'en-tête) ; seuls les exports du Dirigeant s'y lisent (2026-10-29)
    roles: [ROLE_ADMIN, ROLE_USER],
  },
  {
    path: PROFILE_PATH,
    label: 'Mon profil',
    description: 'Vos informations personnelles',
    icon: UserRound,
    section: 'account',
    roles: [ROLE_ADMIN, ROLE_USER],
  },
  {
    path: PASSWORD_PATH,
    label: 'Mon mot de passe',
    description: 'Mot de passe local, utilisable quand la connexion via votre entreprise (SSO) est indisponible',
    icon: KeyRound,
    section: 'account',
    roles: [ROLE_ADMIN, ROLE_USER],
  },
];

export function getNavItems(roles: string[]): NavItem[] {
  return NAV_ITEMS.filter((item) => item.roles.some((role) => roles.includes(role)));
}

/** Page de navigation d'un chemin, y compris ses sous-pages (/users/12 => Utilisateurs) */
export function findNavItem(path: string, items: NavItem[] = NAV_ITEMS): NavItem | undefined {
  return items.find((item) => path === item.path || path.startsWith(`${item.path}/`));
}

/** Accès à une page ou à l'une de ses sous-pages (ex. fiche d'un utilisateur) */
export function canAccess(path: string, roles: string[]): boolean {
  return !!findNavItem(path, getNavItems(roles));
}

/** Page réservée à un rôle dans la société sélectionnée : accessible ou non selon la société choisie */
export function isCompanyScoped(path: string): boolean {
  return !!findNavItem(path)?.roles.every((role) => role.startsWith(COMPANY_ROLE_PREFIX) || role === ROLE_TEAM_MANAGER);
}

/**
 * Page d'arrivée après connexion : la première page accessible (Tableau de bord, sinon Feuille d'heure). Avec les rôles
 * dans la société sélectionnée (useAccessRoles) : la Feuille d'heure en dépend.
 */
export function getHomePath(roles: string[]): string | null {
  // Pas la page des exports (ouverte depuis l'en-tête) : une page du menu, ou le compte
  return getNavItems(roles).find((item) => item.section !== 'header')?.path ?? null;
}
